import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { SERIES_PALETTE } from './chartTheme';
import { CompositionDonutTooltip } from './ChartTooltip';

const RADIAN = Math.PI / 180;

/**
 * Place tooltip on the outer rim of the active slice.
 * Recharts' default uses middleRadius + centered box, which covers the hole on large slices.
 */
export function tipPointFromSlice(slice, pad = 16) {
  if (slice?.cx == null || slice?.cy == null || slice?.midAngle == null || slice?.outerRadius == null) {
    return null;
  }
  const midAngle = Number(slice.midAngle);
  const r = Number(slice.outerRadius) + pad;
  return {
    x: Number(slice.cx) + r * Math.cos(-midAngle * RADIAN),
    y: Number(slice.cy) + r * Math.sin(-midAngle * RADIAN),
    /** Push tooltip outward: below when on the lower arc, above when on the upper arc. */
    placeBelow: Math.sin(-midAngle * RADIAN) > 0.15,
  };
}

/**
 * Donut with legend always BELOW the ring (never overlapping center label).
 *
 * `chartSize` controls the pie area only; legend is extra height underneath.
 * data: [{ name, value, color? }]
 */
export default function CompositionDonut({
  data = [],
  /** Diameter area for the pie itself (not including legend). */
  chartSize = 180,
  innerRadius,
  outerRadius,
  showLegend = true,
  valueFormatter,
  centerLabel,
  centerValue,
  /** @deprecated use chartSize — kept so older call sites still work */
  height,
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(null);

  const rows = useMemo(
    () => (data || []).filter((d) => Number(d.value) > 0),
    [data],
  );
  if (!rows.length) return null;

  // Fit the ring tightly — less empty band above the legend.
  const pieBox = Number(height) > 0 ? Math.max(132, Number(height) - (showLegend ? 52 : 0)) : chartSize;
  const outer = outerRadius ?? Math.round(pieBox * 0.44);
  const inner = innerRadius ?? Math.round(outer * 0.64);

  const total = rows.reduce((s, d) => s + Number(d.value || 0), 0);
  const colored = rows.map((entry, i) => ({
    ...entry,
    color: entry.color || SERIES_PALETTE[i % SERIES_PALETTE.length],
  }));

  const tip = hover ? tipPointFromSlice(hover) : null;

  return (
    <div className="flex w-full flex-col items-stretch">
      <div
        className="relative mx-auto w-full max-w-[260px] overflow-visible outline-none"
        style={{ height: pieBox }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }} style={{ outline: 'none' }}>
            <Pie
              data={colored}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={inner}
              outerRadius={outer}
              paddingAngle={colored.length > 1 ? 2 : 0}
              stroke={colored.length > 1 ? '#fff' : colored[0].color}
              strokeWidth={colored.length > 1 ? 2 : 0}
              isAnimationActive={false}
              rootTabIndex={-1}
              onMouseEnter={(data) => {
                if (data) setHover(data);
              }}
              onMouseMove={(data) => {
                if (data) setHover(data);
              }}
              onMouseLeave={() => setHover(null)}
            >
              {colored.map((entry, i) => (
                <Cell key={entry.name || i} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {(centerLabel || centerValue != null) && (
          <div className="pointer-events-none absolute inset-0 z-0 flex flex-col items-center justify-center px-3">
            {centerValue != null && (
              <span className="text-xl font-bold tabular-nums leading-none text-slate-900">
                {centerValue}
              </span>
            )}
            {centerLabel && (
              <span className="mt-1 max-w-[8rem] truncate text-center text-[11px] font-medium text-slate-500">
                {centerLabel}
              </span>
            )}
          </div>
        )}

        {hover && tip && (
          <div
            className="pointer-events-none absolute z-20"
            style={{
              left: tip.x,
              top: tip.y,
              transform: tip.placeBelow ? 'translate(-50%, 10%)' : 'translate(-50%, -110%)',
            }}
            role="tooltip"
          >
            <CompositionDonutTooltip
              active
              total={total}
              valueFormatter={valueFormatter}
              payload={[
                {
                  name: hover.name,
                  value: hover.value,
                  payload: {
                    color: hover.color || hover.fill || hover.payload?.color,
                  },
                },
              ]}
            />
          </div>
        )}
      </div>

      {showLegend && (
        <ul
          className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-slate-100 pt-3"
          aria-label={t('charts.legend', { defaultValue: 'Chart legend' })}
        >
          {colored.map((entry) => {
            const pct = total ? Math.round((Number(entry.value) / total) * 100) : 0;
            return (
              <li
                key={entry.name}
                className="inline-flex max-w-full items-center gap-1.5 text-xs text-slate-600"
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: entry.color }}
                  aria-hidden
                />
                <span className="truncate font-medium text-slate-700" title={entry.name}>
                  {entry.name}
                </span>
                <span className="shrink-0 tabular-nums text-slate-500">
                  {valueFormatter ? valueFormatter(entry.value) : entry.value}
                  <span className="text-slate-400"> ({pct}%)</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
